import { useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'

import Page from '@shell/Page'
import Button from '@ui/buttons/Button'
import Header from '@ui/elements/Header'
import { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import { useLogout } from 'hooks/useLogout'
import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'

import PersonalModal from '@user/customer/modals/Account/Personal/PersonalModal'
import ContactModal from '@user/customer/modals/Account/Contact/ContactModal'
import AddressModal from '@user/customer/modals/Account/Address/AddressModal'
import InvoiceModal from '@user/customer/modals/Account/Invoice/InvoiceModal'
import CreditsModal from '@user/customer/modals/Account/Credits/CreditsModal'
import { PoliciesModal } from '@user/customer/policies'

import List from './List'
import Details from './Details/Details'
import { ModalState } from './types'
import { colors } from '@ui/colors'
import cn from '@ui/utils/cn'

export default function CustomerAccount() {
  const { t } = useTranslation(['common', 'ui', 'customer'])
  const text = 'customer:account'

  const [modal, setModal] = useState<ModalState>(null)
  const resetModal = () => setModal(null)
  const logout = useLogout() // should we add logoutCutomer and redirect to staff panel instead?

  const { isConfirmed, handleConfirm } = useConfirmCustomer()
  const handleConfirmPress = () => {
    handleConfirm({
      onPoliciesRequired: () => setModal('policies'),
    })
  }

  const viewProps = {
    modal,
    setModal,
  }

  return (
    <>
      <Page stage="customer">
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        <Details {...viewProps} />
        <List {...viewProps} />
        <View
          className="flex w-full flex-row items-center justify-center gap-16"
          style={{ paddingTop: 128 }}
        >
          <View className="flex flex-1 items-end">
            <Button
              variant="outlined"
              label={!isConfirmed ? t('ui:confirm') : t('ui:confirmed')}
              iconEnd={isConfirmed ? 'circle-check-filled' : 'circle'}
              onPress={handleConfirmPress}
              className={cn(isConfirmed && 'border-green-600')}
              overrideColorFront={isConfirmed ? colors.green[600] : undefined}
              colors={isConfirmed ? [colors.green0, colors.green10] : undefined}
              noninteractive={isConfirmed}
            />
          </View>
          <View className="flex flex-1 items-start">
            <Button
              variant="outlined"
              iconEnd="logout"
              label={t('ui:logout')}
              onPress={logout}
              className="border-red-600"
              overrideColorFront={colors.red[600]}
              colors={[colors.red0, colors.red10]}
            />
          </View>
        </View>
        <SpaceButtons />
      </Page>

      <PersonalModal open={modal === 'personal'} close={resetModal} />
      <ContactModal open={modal === 'contact'} close={resetModal} />
      <AddressModal open={modal === 'address'} close={resetModal} />
      <InvoiceModal open={modal === 'invoice'} close={resetModal} />
      <CreditsModal open={modal === 'credits'} close={resetModal} />
      <PoliciesModal open={modal === 'policies'} close={resetModal} />
    </>
  )
}
